
import { lazy } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { Suspense } from 'react';

// Lazy‑load pages with a fallback to avoid rendering issues while loading
const lazyWithFallback = (importFn: () => Promise<{ default: React.ComponentType<any> }>) =>
  lazy(() => importFn());

const HomePage = lazyWithFallback(() => import('../pages/home/page'));
const AboutPage = lazyWithFallback(() => import('../pages/about/page'));
const CountriesPage = lazyWithFallback(() => import('../pages/countries/page'));
const IndiaPage = lazyWithFallback(() => import('../pages/india/page'));
const CambodiaPage = lazyWithFallback(() => import('../pages/cambodia/page'));
const PakistanPage = lazyWithFallback(() => import('../pages/pakistan/page'));
const KidneyTransplantPage = lazyWithFallback(() => import('../pages/kidney-transplant/page'));
const DialysisVsKidneyTransplantPage = lazyWithFallback(() =>
  import('../pages/dialysis-vs-kidney-transplant/page')
);
const DialysisVsKidneyTransplantCostPage = lazyWithFallback(() =>
  import('../pages/dialysis-vs-kidney-transplant-cost/page')
);
const KidneyTransplantCostAbroadPage = lazyWithFallback(() =>
  import('../pages/kidney-transplant-cost-abroad/page')
);
const TransplantWaitingTimeAbroadPage = lazyWithFallback(() =>
  import('../pages/transplant-waiting-time-abroad/page')
);
const CompleteGuideKidneyTransplantAbroadPage = lazyWithFallback(() =>
  import('../pages/complete-guide-kidney-transplant-abroad/page')
);
const ProcessPage = lazyWithFallback(() => import('../pages/process/page'));
const DecisionChecklistPage = lazyWithFallback(() =>
  import('../pages/decision-checklist/page')
);
const AfterReturnPreparationPage = lazyWithFallback(() =>
  import('../pages/after-return-preparation/page')
);
const PricingPage = lazyWithFallback(() => import('../pages/pricing/page'));
const CostPage = lazyWithFallback(() => import('../pages/cost/page'));
const CompanyPage = lazyWithFallback(() => import('../pages/company/page'));
const PrivacyPage = lazyWithFallback(() => import('../pages/privacy/page'));
const ContactPage = lazyWithFallback(() => import('../pages/contact/page'));
const ContactThanksPage = lazyWithFallback(() => import('../pages/contact/thanks/page'));
const WhoThisIsForPage = lazyWithFallback(() => import('../pages/who-this-is-for/page'));
const IndiaNigeriaPage = lazyWithFallback(() => import('../pages/india-nigeria/page'));
const EthicsPage = lazyWithFallback(() => import('../pages/ethics/page'));
const NotFoundPage = lazyWithFallback(() => import('../pages/NotFound'));
const GonePage = lazyWithFallback(() => import('../pages/Gone'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <Suspense fallback={null}>
        <HomePage />
      </Suspense>
    ),
  },
  {
    path: '/about',
    element: (
      <Suspense fallback={null}>
        <AboutPage />
      </Suspense>
    ),
  },
  {
    path: '/countries',
    element: (
      <Suspense fallback={null}>
        <CountriesPage />
      </Suspense>
    ),
  },
  {
    path: '/india',
    element: (
      <Suspense fallback={null}>
        <IndiaPage />
      </Suspense>
    ),
  },
  {
    path: '/cambodia',
    element: (
      <Suspense fallback={null}>
        <CambodiaPage />
      </Suspense>
    ),
  },
  {
    path: '/pakistan',
    element: (
      <Suspense fallback={null}>
        <PakistanPage />
      </Suspense>
    ),
  },
  {
    path: '/pakistan-cost',
    element: <Navigate to="/pakistan" replace />,
  },
  {
    path: '/kidney-transplant',
    element: (
      <Suspense fallback={null}>
        <KidneyTransplantPage />
      </Suspense>
    ),
  },
  {
    path: '/dialysis-vs-kidney-transplant',
    element: (
      <Suspense fallback={null}>
        <DialysisVsKidneyTransplantPage />
      </Suspense>
    ),
  },
  {
    path: '/dialysis-vs-kidney-transplant-cost',
    element: (
      <Suspense fallback={null}>
        <DialysisVsKidneyTransplantCostPage />
      </Suspense>
    ),
  },
  {
    path: '/kidney-transplant-cost-abroad',
    element: (
      <Suspense fallback={null}>
        <KidneyTransplantCostAbroadPage />
      </Suspense>
    ),
  },
  {
    path: '/transplant-waiting-time-abroad',
    element: (
      <Suspense fallback={null}>
        <TransplantWaitingTimeAbroadPage />
      </Suspense>
    ),
  },
  {
    path: '/complete-guide-kidney-transplant-abroad',
    element: (
      <Suspense fallback={null}>
        <CompleteGuideKidneyTransplantAbroadPage />
      </Suspense>
    ),
  },
  {
    path: '/process',
    element: (
      <Suspense fallback={null}>
        <ProcessPage />
      </Suspense>
    ),
  },
  {
    path: '/decision-checklist',
    element: (
      <Suspense fallback={null}>
        <DecisionChecklistPage />
      </Suspense>
    ),
  },
  {
    path: '/after-return-preparation',
    element: (
      <Suspense fallback={null}>
        <AfterReturnPreparationPage />
      </Suspense>
    ),
  },
  {
    path: '/pricing',
    element: (
      <Suspense fallback={null}>
        <PricingPage />
      </Suspense>
    ),
  },
  {
    path: '/cost',
    element: (
      <Suspense fallback={null}>
        <CostPage />
      </Suspense>
    ),
  },
  {
    path: '/company',
    element: (
      <Suspense fallback={null}>
        <CompanyPage />
      </Suspense>
    ),
  },
  {
    path: '/privacy',
    element: (
      <Suspense fallback={null}>
        <PrivacyPage />
      </Suspense>
    ),
  },
  {
    path: '/contact',
    element: (
      <Suspense fallback={null}>
        <ContactPage />
      </Suspense>
    ),
  },
  {
    path: '/contact/thanks',
    element: (
      <Suspense fallback={null}>
        <ContactThanksPage />
      </Suspense>
    ),
  },
  {
    path: '/who-this-is-for',
    element: (
      <Suspense fallback={null}>
        <WhoThisIsForPage />
      </Suspense>
    ),
  },
  {
    path: '/india-nigeria',
    element: (
      <Suspense fallback={null}>
        <IndiaNigeriaPage />
      </Suspense>
    ),
  },
  {
    path: '/ethics',
    element: (
      <Suspense fallback={null}>
        <EthicsPage />
      </Suspense>
    ),
  },
  {
    path: '/en/cost',
    element: <Navigate to="/cost" replace />,
  },
  {
    path: '/vitalhealth-chronicles',
    element: <GonePage />,
  },
  {
    path: '/vitalhealth-chronicles/*',
    element: <GonePage />,
  },
  {
    path: '/f',
    element: <GonePage />,
  },
  {
    path: '/f/*',
    element: <GonePage />,
  },
  {
    path: '/international-kidney-transplant-information',
    element: (
      <Navigate to="/complete-guide-kidney-transplant-abroad" replace />
    ),
  },
  {
    path: '*',
    element: (
      <Suspense fallback={null}>
        <NotFoundPage />
      </Suspense>
    ),
  },
];

export default routes;
